import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to manage clip likes with localStorage + Supabase.
 *
 * Pattern: Follows useShortsLikes.js —
 *   - localStorage tracks which clips the user has liked
 *   - Supabase `lecture_clips` table stores aggregate like_count
 *   - Optimistic UI: toggle immediately, sync to Supabase in background
 *   - Graceful fallback if Supabase fails (localStorage is source of truth for user state)
 *
 * @param {string|null} clipId - UUID of the current clip
 * @returns {{ likeCount, hasLiked, toggleLike }}
 */
const STORAGE_KEY = 'clips_liked_list';

const getLikedClips = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch (_) {
    return [];
  }
};

const saveLikedClips = (list) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch (_) {}
};

const useClipLikes = (clipId) => {
  const [likeCount, setLikeCount] = useState(null);
  const [hasLiked, setHasLiked] = useState(false);

  // Check localStorage on mount / clip change
  useEffect(() => {
    if (!clipId) return;
    const liked = getLikedClips();
    setHasLiked(liked.includes(clipId));
  }, [clipId]);

  // Fetch current like count from Supabase
  useEffect(() => {
    if (!clipId) return;

    const fetchCount = async () => {
      try {
        const { supabase } = await import('../../../lib/supabase');
        const { data, error } = await supabase
          .from('lecture_clips')
          .select('like_count')
          .eq('id', clipId)
          .single();

        if (!error && data) {
          setLikeCount(data.like_count);
        }
      } catch (_) {}
    };

    fetchCount();
  }, [clipId]);

  // Toggle like
  const toggleLike = useCallback(async () => {
    if (!clipId) return;

    const liked = getLikedClips();
    const isCurrentlyLiked = liked.includes(clipId);

    // Optimistic update
    if (isCurrentlyLiked) {
      saveLikedClips(liked.filter(id => id !== clipId));
      setHasLiked(false);
      setLikeCount(prev => Math.max((prev ?? 1) - 1, 0));
    } else {
      saveLikedClips([...liked, clipId]);
      setHasLiked(true);
      setLikeCount(prev => (prev ?? 0) + 1);
    }

    // Sync to Supabase
    try {
      const { supabase } = await import('../../../lib/supabase');
      const rpcName = isCurrentlyLiked ? 'decrement_clip_like' : 'increment_clip_like';
      await supabase.rpc(rpcName, { p_clip_id: clipId });
    } catch (err) {
      console.warn('[useClipLikes] Supabase sync failed (localStorage is source of truth):', err);
    }
  }, [clipId]);

  return { likeCount, hasLiked, toggleLike };
};

export default useClipLikes;
